/*
 * Scenario: mixed-sequence
 *
 * Ein Rewrite, ein Insert, ein Delete — alle drei Vorschlags-Arten in
 * einem einzigen Agent-Turn. Stress-Test fuer das Multi-Overlay-Rendering:
 * der ReviewController muss alle drei Kinds zeitgleich anzeigen, der
 * View-Hint muss `dimAllExcept` korrekt zusammenstellen.
 *
 * Anker-Paragraphen (Reihenfolge wie textParagraphs):
 *   [0] rewrite
 *   [1] insert nach diesem
 *   [3] delete    (Index 2 wird uebersprungen, damit Insert und Delete
 *                  nicht denselben Paragraphen anfassen)
 */

import { emit, wait } from './_helpers.js'

export const name = 'mixed-sequence'
export const description = 'Rewrite + insert + delete in one turn — all three overlays simultaneously.'

export async function run(state) {
    await emit(state, 'session', { sessionId: 'mock-mixed' }, 0)
    await emit(state, 'thinking', { text: 'Three different changes — let me show them together.' }, 250)
    await emit(state, 'thinkingDone', {}, 200)

    const paras = state.context?.cursorContext?.textParagraphs || []
    if (paras.length < 4) {
        await emit(state, 'delta', { text: 'Need at least four text paragraphs for this demo.' }, 100)
        await emit(state, 'done', { usage: {} }, 100)
        return
    }

    const rewriteTarget = paras[0]
    const insertAfter = paras[1]
    const deleteTarget = paras[3]

    const rewriteId = 'tu_mix_rw_' + Date.now().toString(36)
    const insertId = 'tu_mix_in_' + Date.now().toString(36)
    const deleteId = 'tu_mix_de_' + Date.now().toString(36)

    await emit(state, 'proposeRewrite', {
        toolCallId: rewriteId,
        paragraphId: rewriteTarget.id,
        baseVersion: rewriteTarget.version,
        originalText: rewriteTarget.text,
        newText: 'The morning broke clear and cold — the kind of cold that finds the bones before the skin.',
        reason: 'Sharper opening; sensory specificity.'
    }, 300)

    await emit(state, 'proposeInsert', {
        toolCallId: insertId,
        afterParagraphId: insertAfter.id,
        baseVersion: insertAfter.version,
        newText: 'Outside, the gulls had stopped their wheeling. Even they knew, perhaps, what was coming.',
        reason: 'Adds a beat of foreboding before the action begins.'
    }, 300)

    await emit(state, 'proposeDelete', {
        toolCallId: deleteId,
        paragraphId: deleteTarget.id,
        baseVersion: deleteTarget.version,
        reason: 'Repeats the previous beat with no new information.'
    }, 300)

    await emit(state, 'done', { usage: {} }, 200)

    try {
        const results = await Promise.all([
            wait(state, rewriteId, 5 * 60 * 1000),
            wait(state, insertId, 5 * 60 * 1000),
            wait(state, deleteId, 5 * 60 * 1000)
        ])
        const [rw, ins, del] = results
        const parts = [
            `rewrite ${rw.result}`,
            `insert ${ins.result}`,
            `delete ${del.result}`
        ]
        await emit(state, 'delta', { text: `Done — ${parts.join(', ')}.` }, 300)
        await emit(state, 'done', { usage: {} }, 100)
    } catch { /* abort */ }
}
